// src/components/LengthRow.js
import React, { useRef, useEffect } from 'react';

const LengthRow = ({ length, size = 'large', progress = 0, tier = 'default', milestones = {} }) => {
    const prevProgressRef = useRef(progress);
    useEffect(() => {
        prevProgressRef.current = progress;
    });
    const prevProgress = prevProgressRef.current;
    
    const getTileSizeClass = () => {
        if (size === 'small') {
            if (length > 12) return 'w-6 h-6 sm:w-8 sm:h-8';
            if (length > 10) return 'w-7 h-7 sm:w-9 sm:h-9';
            return 'w-8 h-8 sm:w-10 sm:h-10';
        }
        return 'w-10 h-10 sm:w-14 sm:h-14';
    };

    const getFontSizeClass = () => {
        if (size === 'small') {
            if (length > 12) return 'text-xs sm:text-sm';
            if (length > 10) return 'text-sm sm:text-base';
            return 'text-lg sm:text-xl';
        }
        return 'text-2xl sm:text-3xl';
    };

    const getSpacingClass = () => {
        if (size === 'small' && length > 10) return 'space-x-0.5 sm:space-x-1';
        return 'space-x-1 sm:space-x-2';
    }

    const getFillColorClass = () => {
        switch (tier) {
            case 'bronze': return 'bg-bronze text-white border-bronze';
            case 'silver': return 'bg-silver text-gray-800 border-silver';
            case 'gold': return 'bg-gold text-gray-900 border-gold';
            case 'platinum': return 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-transparent';
            default: return 'bg-correct text-white border-correct';
        }
    };

    const getMilestoneEmoji = (type) => {
        switch (type) {
            case 'bronze': return '🥉';
            case 'silver': return '🥈';
            case 'gold': return '🥇';
            case 'platinum': return '🏆';
            default: return '';
        }
    };

    const getMilestoneOverlayColor = (type) => {
        switch (type) {
           case 'bronze': return 'bg-bronze/30 dark:bg-bronze/40 border-bronze text-bronze';
           case 'silver': return 'bg-silver/30 dark:bg-silver/40 border-silver text-gray-500 dark:text-gray-300';
           case 'gold': return 'bg-gold/30 dark:bg-gold/40 border-gold text-yellow-600 dark:text-yellow-500';
           case 'platinum': return 'bg-purple-100 dark:bg-purple-900/40 border-purple-300 text-purple-600 dark:text-purple-300';
           default: return '';
       }
   };

    const fillColorClass = getFillColorClass();

    return (
        <div className={`flex justify-center items-center ${getSpacingClass()} ${getFontSizeClass()}`}>
            {Array.from({ length: length || 0 }).map((_, index) => {
                const tileNumber = index + 1;
                const isFilled = index < progress;
                const isNewlyFilled = index === progress - 1 && progress > prevProgress;
                const milestoneType = milestones[tileNumber];
                
                let tileClasses = `font-bold uppercase flex items-center justify-center ${getTileSizeClass()} rounded-md shadow-md transition-all duration-500 relative border-2 `;

                if (isFilled) {
                    tileClasses += `${fillColorClass} `;
                } else {
                    tileClasses += 'bg-white dark:bg-dark-key-bg border-transparent ';
                     if (milestoneType) {
                        tileClasses += getMilestoneOverlayColor(milestoneType);
                     }
                }

                if (isNewlyFilled) {
                    tileClasses += 'animate-pop-in ';
                }

                return (
                    <span key={index} className={tileClasses}>
                        {milestoneType ? (
                            <div className="relative flex items-center justify-center w-full h-full">
                                {isFilled ? (
                                    tileNumber
                                ) : (
                                    <span className="text-[1.2em] sm:text-[1.2em] text-[0.9em] filter drop-shadow-sm transform hover:scale-110 transition-transform">
                                        {getMilestoneEmoji(milestoneType)}
                                    </span>
                                )}
                                {/* Optional: Small indicator when filled to show it WAS a milestone */}
                                {isFilled && (
                                     <span className="absolute -top-1 -right-1 sm:-top-1 sm:-right-1 text-[0.4em] sm:text-[0.6em] opacity-80 filter drop-shadow-sm">
                                        {getMilestoneEmoji(milestoneType)}
                                     </span>
                                )}
                            </div>
                        ) : (
                            tileNumber
                        )}
                    </span>
                );
            })}
        </div>
    );
};

export default LengthRow;
